import React, { useEffect, useMemo } from "react";
import { Button, ButtonGroup, TextLink } from "@vds/buttons";
import { Input } from "antd";
import Search from "@mui/icons-material/Search";
import { Box } from "@mui/system";
import { Tooltip } from "@mui/material";
import {
  AddUserToGroupModal,
  CreateUserModal,
  DeleteUserGroupModal,
} from "./UserGroupsModals";
import { useState } from "react";
import { getRandomUUID } from "../../Token";
import {
  addUsersToNotificationGroupsEndPoint,
  createNotificationUsersEndPoint,
  deleteNotificationUsersEndPoint,
  fetchNotificationUsersEndPoint,
  updateNotificationUsersEndPoint,
} from "../../configuration";
import { Loader } from "@vds/loaders";
import { searchTableRecordByInput } from "../../global/utility/tableUtils";
import { useNavigate, useLocation } from "react-router-dom";
import InviteUserModal from "./InviteUserModal";
import { loadUserGroupsTable } from "../../api/UserGroupsServiceManager";
import { useSelector } from "react-redux";
import { Notification } from "@vds/notifications";
import NotificationBanner from "../../layouts/NotificationBanner";
import { hasPermission } from "../../global/utility/permissionUtils";
import CustomHOCTable from "../../global/components/Tables/CustomHOCTable";
import { Body, Title } from "@vds/typography";
import { getFilterOptions } from "../../global/utility/deviceTableUtils";
import { Icon } from "@vds/icons";
import { PAGE_IDENTIFIERS } from "../../global/constants/tableConstants";
import { USER_GROUP_DETAILS_CONSTANTS, DELETE_GROUP_MODAL_CONSTANTS } from "../../global/constants/constants";
import { setDetailNavSource } from "../../hooks/usePaginationReset";
import moment from "moment-timezone";
import {
  showNotificationBanner,
  roleList as allRolesList,
} from "../../store/actions";
import { useDispatch } from "react-redux";
import axiosInstance from "../../global/axiosInterceptor";
import { getRoles } from "../../model/roleService";
import { handleApiError, getDateforUsers } from "../../global/utility/Utils";
import "../Users/Users.css";
import { sortAlphabetically, sortByDate, sortByFormattedDate, SORT_DIRECTIONS } from "../../global/utility/sortingUtils";

const NotificationUsers = (props) => {
  const location = useLocation();
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [notificationUsersDataSource, setNotificationUsersDataSource] =
    useState(null);
  const [notificationDataSource, setNotificationDataSource] = useState(null);
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const { roleList } = useSelector((state) => state?.userRoleReducer);
  const [rolesList, setRolesList] = useState([]); // To store the fetched roles
  const [siteList, setSiteList] = useState([]);

  useEffect(() => {
    setSiteList(siteTagList?.sites);
    setRolesList(roleList);
  }, []);

  useEffect(() => {
    const roleResponse = async () => {
      try {
        const roleList = await getRoles(activeSession);
        if (roleList) {
          setRolesList(roleList);
        } else {
          setRolesList([]);
        }
        dispatch(allRolesList(roleList));
      } catch (error) {
        console.log("Getting error", error);
      }
    };

    roleResponse();
  }, [activeSession]);

  const pageIdentifier = PAGE_IDENTIFIERS.USER_TABLE;
  const {
    notificationGroupList,
    notificationUsersList,
    setGroupFormFields,
    defaultGroupFormFields,
    loading,
    setShowNotificationPopup,
    setNotificationUsersList,
    setShowNotification,
    showNotification,
    bannerType,
    userList,
    setBannerType,
  } = props;
  const columns = useMemo(() => {
    return [
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            User
          </Title>
        ),
        dataIndex: "User",
        key: "User",
        showCheckBox: false,
        width: "25%",
        sorter: (a, b) => sortAlphabetically(a, b, "User"),
        sortDirections: SORT_DIRECTIONS,
        render: (text, record) => {
          return (
            <TextLink
              type="standAlone"
              size="large"
              className="link"
              style={{
                width: "100%",
                display: "block",
                borderBottomStyle: "none",
                cursor: "pointer",
                textDecoration: "underline",
                overflow: "visible",
                wordBreak: "break-word",
                lineHeight: "1.2",
              }}
              data-testid="notificationUsers_textLinkUsersButton"
              onClick={() => handleGroupInfo(record)}
            >
              <Body size="large" viewport="desktop" primitive="span">
                {text}
              </Body>
            </TextLink>
          );
        },
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            Email
          </Title>
        ),
        dataIndex: "email",
        key: "email",
        width: "20%",
        sorter: (a, b) => sortAlphabetically(a, b, "email"),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            Role
          </Title>
        ),
        dataIndex: "Role",
        key: "Role",
        width: "12%",
        sorter: (a, b) => sortAlphabetically(a, b, "Role"),
        sortDirections: SORT_DIRECTIONS,
        render: (text, record) => (
          <Body size="large" viewport="desktop" primitive="span">
            {text || "Not Available"}
          </Body>
        ),
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            Last Login
          </Title>
        ),
        dataIndex: "LastLogin",
        key: "LastLogin",
        width: "18%",
        sorter: (a, b) => sortByDate(a, b, "LastLogin"),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        dataIndex: "site",
        key: "site",
        width: "0px",
        isFilter: true,
        filter: getFilterOptions(siteList, "site"),
        onFilter: (value, record) => record.site.id === value,
        onHeaderCell: () => ({
          style: { padding: 0, borderLeft: 'none', borderRight: 'none' }
        }),
        onCell: () => ({
          style: { padding: 0, borderLeft: 'none', borderRight: 'none' }
        })
      },
      {
        dataIndex: "role",
        key: "role",
        width: "0px",
        isFilter: true,
        filter: getFilterOptions(rolesList, "role"),
        onFilter: (value, record) => record.role?.id === value,
        onHeaderCell: () => ({
          style: { padding: 0, borderLeft: 'none', borderRight: 'none' }
        }),
        onCell: () => ({
          style: { padding: 0, borderLeft: 'none', borderRight: 'none' }
        })
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            Date Added
          </Title>
        ),
        dataIndex: "DateAdded",
        key: "DateAdded",
        width: "15%",
        sorter: (a, b) => sortByFormattedDate(a, b, "DateAdded"),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: (
          <Title size="small" bold primitive="h2" viewport="desktop">
            Actions
          </Title>
        ),
        dataIndex: "Actions",
        key: "Actions",
        width: "10%",
        render: (text, record) => {
          return {
            children: (
              <div className="users-tab-actions">
                <Tooltip title={USER_GROUP_DETAILS_CONSTANTS.TOOLTIP_DELETE_USER} arrow placement="top">
                  <div
                    className="action-btn-wrapper"
                    data-testid="notificationUsers_trashButton"
                    onClick={() => deleteUser(record.id, record.User)}
                  >
                    <Icon name="trash" size="medium" color="#000000" ariaHidden={true} />
                  </div>
                </Tooltip>
              </div>
            ),
          };
        },
      },
    ];
  }, [notificationUsersDataSource]);
  const defaultUserFormFields = {
    firstName: "",
    lastName: "",
    contactNumber: "",
    emailID: "",
    role: "",
    groupid: "",
    selectedUserGroups: [],
  };
  const [searchNotificationUsers, setSearchNotificationUsers] = useState("");
  const [usersId, setUsersId] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState({
    isOpen: false,
    isUserLink: false,
  });
  const [userFormFields, setUserFormFields] = useState(defaultUserFormFields);
  const [showAddToGroup, setShowAddToGroup] = useState({
    isOpen: false,
    isLoading: false,
  });
  const [addToGroupId, setAddToGroupId] = useState(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState({
    isOpen: false,
  });
  const [showViewGroups, setShowViewGroups] = useState({ isOpen: false });
  const [currentUserGroups, setCurrentUserGroups] = useState([]);
  const [updatedGroupIds, setUpdateGroupIds] = useState({
    addGroupIds: [],
    removeGroupIds: [],
  });
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [refreshUsersTable, setRefreshUsersTable] = useState(false);
  const [notification, setNotification] = useState({
    opened: false,
    type: "warning",
    message: "Sorry, this page is under construction. Please come back later!",
  });
  const [usersList, setUsersList] = useState(userList || []);
  const [isDataLoading, setIsDataLoading] = useState(loading);

  useEffect(() => {
    if (notification.opened) {
      const hideNotification = setTimeout(() => {
        setNotification({ ...notification, opened: false });
      }, 3000);

      return () => clearTimeout(hideNotification);
    }
  }, [notification]);

  const handleAddToSite = () => {
    setNotification({ ...notification, opened: true });
  };

  const handleOpenModal = () => {
    if (!hasPermission("User management", "Create")) {
      setPermissionBanner();
      showPermissionsBanner();
    } else {
      setShowInviteUserModal({ ...showInviteUserModal, isOpen: true });
    }
  };

  const fetchNotificationUser = async () => {
    const response = await fetch(fetchNotificationUsersEndPoint(), {
      method: "POST",
      body: JSON.stringify({
        accountName: activeSession.accountId,
        filter: {$expand: "roles"}
      }),
      headers: {
        "Content-Type": "application/json",
        ...(activeSession.sessionToken && { Authorization: `Bearer ${activeSession.sessionToken}` }),
      },
    });
    const userResponse = await response.json();
    setNotificationUsersList(userResponse);
    setUsersList(userResponse);
    setRefreshUsersTable(false);
  };

  useEffect(() => {
    if (refreshUsersTable) {
      fetchNotificationUser();
    }
  }, [refreshUsersTable]);

  const getSiteDetails = (userItem) => {
    const siteNames =
      userItem?.tags && userItem?.tags?.length > 1
        ? `Multiple Sites (${userItem.tags.length})`
        : userItem?.tags?.length === 1
        ? userItem?.tags[0]?.name
        : "Not Available";
    return siteNames;
  };

  const getNotificationUsersDataSource = (notificationUsersList) => {
    return notificationUsersList?.map((notificationUsersItem) => ({
      User: `${notificationUsersItem?.firstname} ${notificationUsersItem.lastname}`,
      Site: getSiteDetails(notificationUsersItem),
      Role: notificationUsersItem?.customdata?.roles?.length > 0 ? notificationUsersItem.customdata.roles[0].name : "Not Available",
      LastLogin: moment(notificationUsersItem?.lastupdated).tz(moment.tz.guess()).format(
        "h:mm A, MMM. D, YYYY z"
      ),
      DateAdded: notificationUsersItem?.createdon
        ? getDateforUsers(notificationUsersItem)
        : "",
      Actions: "",
      id: notificationUsersItem.id,
      ...notificationUsersItem,
    }));
  };

  useEffect(() => {
    setIsDataLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (!notificationUsersList?.length) {
      // Only clear data source when not loading (fetch completed with empty result)
      if (!loading) setNotificationUsersDataSource([]);
      return;
    }
    const notificationUsersData = getNotificationUsersDataSource(notificationUsersList);
    setNotificationUsersDataSource(notificationUsersData);
    setIsDataLoading(false);
  }, [notificationUsersList, loading]);

  const addToGroup = () => {
    setShowAddToGroup({ isOpen: true });
    setAddToGroupId(null);
  };

  const handleUserFieldChange = (e, type, record) => {
    const { name, value, checked } = e.currentTarget;
    if (type === "checkbox") {
      let selectedUserGroups = [...userFormFields?.selectedUserGroups];
      if (checked) {
        if (currentUserGroups.find((elm) => record?.id !== elm?.id)) {
          const ids = [
            ...new Set([...updatedGroupIds.addGroupIds, record?.id]),
          ];
          setUpdateGroupIds({
            ...updatedGroupIds,
            addGroupIds: [...ids],
          });
        }
        if (
          updatedGroupIds.removeGroupIds.find(
            (removedId) => removedId === record?.id
          )
        ) {
          const ids = [
            ...new Set([
              ...updatedGroupIds.removeGroupIds.filter(
                (id) => id !== record?.id
              ),
            ]),
          ];
          setUpdateGroupIds({
            ...updatedGroupIds,
            removeGroupIds: [...ids],
          });
        }

        selectedUserGroups = [...selectedUserGroups, record];
      } else {
        if (currentUserGroups.find((elm) => record?.id === elm?.id)) {
          const ids = [
            ...new Set([...updatedGroupIds.removeGroupIds, record?.id]),
          ];
          setUpdateGroupIds({
            ...updatedGroupIds,
            removeGroupIds: [...ids],
          });
        }
        if (
          updatedGroupIds.addGroupIds.find((addedId) => addedId === record?.id)
        ) {
          const ids = [
            ...new Set([
              ...updatedGroupIds.addGroupIds.filter((id) => id !== record?.id),
            ]),
          ];
          setUpdateGroupIds({
            ...updatedGroupIds,
            addGroupIds: [...ids],
          });
        }

        selectedUserGroups = [
          ...selectedUserGroups.filter((item) => item.id !== record?.id),
        ];
      }
      setUserFormFields((prev) => ({
        ...prev,
        selectedUserGroups: [...selectedUserGroups],
      }));
    }
    setUserFormFields((prev) => ({ ...prev, [name]: value }));
    console.log(updatedGroupIds);
  };

  const handleAddGroupCheckBoxChange = (e, record) => {
    const { checked } = e.target;
    console.log(record);
    if (checked) {
      setAddToGroupId({ ...record });
    } else {
      setAddToGroupId(null);
    }
  };
  const handleCheckBox = (e, record) => {
    console.log(usersId);
    const { checked } = e.target;
    if (checked) {
      setUsersId((prev) => [...prev, record?.id]);
    } else {
      setUsersId([...usersId.filter((id) => id !== record?.id)]);
    }
  };

  //pending
  const dynamicPostApiCall = async (endPoint, payload, method) => {
    const response = await axiosInstance[method?.toLowerCase() || "post"](
      endPoint(),
      {
        data: {
          ...payload,
        },
      }
    );
    return response;
  };

  const onSubmit = async (isUpdate) => {
    const {
      firstName: firstname,
      lastName: lastname,
      contactNumber: mdn,
      emailID: email,
      id: id,
      groupid,
      selectedUserGroups,
    } = userFormFields;

    let endPoint = createNotificationUsersEndPoint;
    let method = "POST";
    const payload = {
      groupids: [...selectedUserGroups.map((item) => item?.id)],
      user: {
        firstname,
        lastname,
        mdn,
        email,
      },
    };

    if (isUpdate) {
      delete payload?.groupids;
      console.log(updatedGroupIds);
      payload["id"] = id;
      payload["addGroupIds"] =
        currentUserGroups?.length > 0
          ? [...updatedGroupIds.addGroupIds]
          : [...selectedUserGroups.map((item) => item?.id)];
      payload["removeGroupIds"] =
        currentUserGroups?.length > 0
          ? [...updatedGroupIds.removeGroupIds]
          : [];
      endPoint = updateNotificationUsersEndPoint;
      method = "PATCH";
    }
    setShowCreateUser({ ...showCreateUser, isLoading: true });
    const userPostCallResponse = await dynamicPostApiCall(
      endPoint,
      payload,
      method
    );
    if (userPostCallResponse?.ok) {
      setRefreshUsersTable(true);
      if (usersId && usersId?.length > 0) {
        setUsersId([]);
      }
      setShowCreateUser({ isOpen: false, isLoading: false });
    } else {
      setShowCreateUser({ isLoading: false });
    }
    setUserFormFields(defaultUserFormFields);
  };

  const createUser = async (record, isUserLink) => {
    if (record) {
      setCurrentUserGroups(() => []);
      setUpdateGroupIds({
        addGroupIds: [],
        removeGroupIds: [],
      });
      setShowCreateUser({ isLoading: true });
      const data = await loadUserGroupsTable(record?.id, activeSession);
      if (data?.length >= 0) {
        setCurrentUserGroups([...data]);
      } else {
        setShowCreateUser({ isOpen: false, isLoading: false });
        setShowNotificationPopup({
          message: "Something went wrong, Please try again later.",
          type: "error",
          show: true,
          title: "Something went wrong",
        });
        return;
      }

      setShowCreateUser({
        isOpen: true,
        mode: "edit",
        isLoading: false,
        isUserLink,
      });
      setUserFormFields({
        firstName: record?.firstname,
        lastName: record?.lastname,
        contactNumber: record?.mdn,
        emailID: record?.email,
        role: "NA",
        id: record?.id,
        group: record?.group,
        selectedUserGroups: [...data],
      });
    } else {
      setShowCreateUser({ isOpen: true, isUserLink: false });
    }
  };

  const getValidUsersIdToAddGroup = () =>
    usersId.filter(
      (id) => !addToGroupId?.users?.find((item) => item?.id === id)
    );
  const onAddUser = async () => {
    const finalUsersId = [...getValidUsersIdToAddGroup()];
    if (finalUsersId?.length <= 0) {
      setShowAddToGroup({ isLoading: false, isOpen: false });
      return;
    }

    const payload = {
      id: addToGroupId?.id,
      userids: [...finalUsersId],
    };
    setShowAddToGroup({ isLoading: true });
    let endPoint = addUsersToNotificationGroupsEndPoint;
    let method = "POST";

    const addToGroupCallResponse = await dynamicPostApiCall(
      endPoint,
      payload,
      method
    );
    if (addToGroupCallResponse?.ok) {
      setShowAddToGroup({ isLoading: false, isOpen: false });
      setUsersId([]);
      setRefreshUsersTable(true);
    } else {
      setShowAddToGroup({ isLoading: false, isOpen: false });
      setShowNotificationPopup({
        message: "Something went wrong, Please try again later.",
        type: "error",
        show: true,
        title: "Something went wrong",
      });
    }
  };

  const confirmDelete = async (isConfirm, recordId) => {
    if (!isConfirm) {
      setShowDeleteUserModal({ isOpen: false });
      return;
    }
    const user = notificationUsersList.find((u) => u.id === recordId);
    setShowDeleteUserModal({ ...showDeleteUserModal, isLoading: true });
    if (recordId) {
      try {
        const payload = {
          id: recordId,
        };
        const deleteUserResponse = await dynamicPostApiCall(
          deleteNotificationUsersEndPoint,
          payload,
          "DELETE"
        );
        if (deleteUserResponse?.status === 204) {
          console.log(deleteUserResponse.status, "res status");
          const notificationInfo = {
            type: "Delete User",
            priority: "success",
            data: {
              subTitle: `User ${
                user.firstname + " " + user.lastname
              } has been deleted successfully`,
            },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));
          setShowDeleteUserModal({ isOpen: false, isLoading: false });
          setRefreshUsersTable(true);
          setUsersId([]);
        }
      } catch (error) {
        setShowDeleteUserModal({ isOpen: false, isLoading: false });
        setRefreshUsersTable(true);
        console.error("Failed to remove user, status:", error);
        const errorMessage = error?.response?.data?.error_description;
        handleApiError(
          "warning",
          errorMessage
            ? errorMessage
            : "Failed to delete user list, please try again."
        );
      }
      // const notificationInfo = {
      //   type: "Delete User",
      //   priority: "critical",
      //   data: {
      //     subTitle: `User ${
      //       user.firstname + " " + user.lastname
      //     } hasn't deleted successfully`,
      //   },
      //   showNotification: true,
      // };
      //dispatch(showNotificationBanner(notificationInfo));
    }
  };
  const setPermissionBanner = () => {
    setBannerType({ type: "No Permission", priority: "critical" });
    setShowNotification(true);
  };

  const deleteUser = (record, userName) => {
    if (!hasPermission("User management", "Delete")) {
      setPermissionBanner();
      showPermissionsBanner();
    } else {
      setShowDeleteUserModal({ isOpen: true, record: record, userName: userName });
    }
  };

  const handleGroupInfo = async (record) => {
    // Allow all users to view user details
    // Editing restrictions are enforced in the UserDetails page based on user role
    setDetailNavSource(PAGE_IDENTIFIERS.USER_TABLE);
    navigate("/userdetails", { state: record });
  };

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: "No Permission",
      priority: "critical",
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  useEffect(() => {
    const searchColumns = ["firstname", "lastname", "mdn", "email"];
    const filteredList = notificationUsersList?.filter((userlist) =>
      searchTableRecordByInput(
        userlist,
        searchNotificationUsers,
        searchColumns,
        notificationUsersList
      )
    );
    if (!usersId.find((id) => filteredList.find((item) => item.id === id))) {
      setUsersId([]);
    }
  }, [searchNotificationUsers]);

  useEffect(() => {
    if (!notificationUsersList || notificationUsersList?.length <= 0) {
      setUsersId([]);
    }
  }, [notificationUsersList]);

  useEffect(() => {
    if (location.state?.openInviteModal) {
      setShowInviteUserModal({ ...showInviteUserModal, isOpen: true });
    }
  }, [location.state]);

  const UsersTable = CustomHOCTable("NotificationUsers");
  const InviteUserButton = (
    <Button
      size="medium"
      disabled={false}
      use="primary"
      className="responsive-action-button"
      data-testid="notificationUsers_inviteUserButton"
      onClick={handleOpenModal}
    >
      Invite User
    </Button>
  );

  return (
    <>
      <Loader
        active={
          showCreateUser?.isLoading ||
          showViewGroups?.isLoading ||
          showAddToGroup?.isLoading
        }
        fullscreen={false}
      />
      <InviteUserModal
        title="Invite User"
        showModal={showInviteUserModal}
        setShowModal={setShowInviteUserModal}
        setShowNotificationPopup={setShowNotificationPopup}
        dataTestId="inviteUserNewModal"
        usersList={userList}
        setShowNotification={setShowNotification}
        bannerType={bannerType}
        setBannerType={setBannerType}
        usersSiteList = {siteTagList.sites}
      />
      <AddUserToGroupModal
        notificationGroupList={notificationGroupList}
        showAddToGroup={showAddToGroup}
        setShowAddToGroup={setShowAddToGroup}
        setGroupFormFields={setGroupFormFields}
        defaultGroupFormFields={defaultGroupFormFields}
        handleAddGroupCheckBoxChange={handleAddGroupCheckBoxChange}
        addToGroupId={addToGroupId?.id}
        setAddToGroupId={setAddToGroupId}
        onAddUser={onAddUser}
        getValidUsersIdToAddGroup={getValidUsersIdToAddGroup}
      />
      <DeleteUserGroupModal
        showDeleteUserGroupModal={showDeleteUserModal}
        confirmDelete={confirmDelete}
        message={DELETE_GROUP_MODAL_CONSTANTS.CONFIRM_USER_MESSAGE}
        title="Delete User"
        showWarningBanner={false}
      />
      <UsersTable
        showSearch={true}
        searchInputText={searchNotificationUsers}
        columns={columns}
        dataList={notificationUsersDataSource}
        extraAction={InviteUserButton}
        loading={isDataLoading}
        activeSession={activeSession}
        pageIdentifier={pageIdentifier}
        useReduxPersistence={true}
      />
    </>
  );
};

export default NotificationUsers;
